import React, { useState, useRef, useContext } from "react";
import {
  Box,
  Container,
  Typography,
  Avatar,
  Paper,
  Stack,
  IconButton,
  Button,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  alpha,
  useTheme,
  CircularProgress,
  InputAdornment,
  Switch,
  FormControlLabel,
} from "@mui/material";
import {
  PhotoCamera,
  Save,
  Logout,
  DeleteForever,
  Visibility,
  VisibilityOff,
  Language,
  DarkMode,
  LightMode,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import CustomTextField from "../components/RTLTextField";
import { ColorModeContext } from "../App"; // تأكد من استيراد السياق الخاص بالـ Theme
import { editUser } from "../redux/slices/user/thunk";
import { useDispatch } from "react-redux";
import useBaseImageUrl from "../hooks/useBaseImageUrl";

export default function Profile() {
  const theme = useTheme();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const colorMode = useContext(ColorModeContext); // التحكم في الـ Mode
  const fileInputRef = useRef(null);
  const baseUrl = useBaseImageUrl();

  const isRtl = i18n.language === "ar";
  const brandRed = theme.palette.primary.main;

  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [showPassword, setShowPassword] = useState(false);

  const storedUser = JSON.parse(localStorage.getItem("user")) || {};
  const [formData, setFormData] = useState({
    name: storedUser.name || "",
    email: storedUser.email || "",
    mobile: storedUser.mobile || "",
    password: "",
  });

  const toggleLanguage = () => {
    const newLang = isRtl ? "en" : "ar";
    i18n.changeLanguage(newLang);
    document.dir = newLang === "ar" ? "rtl" : "ltr";
  };

  // دالة ضغط الصور (نفس الكود السابق)
  const processImage = (file) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target.result;
        img.onload = () => {
          const canvas = document.createElement("canvas");
          const ctx = canvas.getContext("2d");
          canvas.width = 400;
          canvas.height = 400;
          ctx.drawImage(img, 0, 0, 400, 400);
          canvas.toBlob(
            (blob) => {
              resolve(new File([blob], "profile.webp", { type: "image/webp" }));
            },
            "image/webp",
            0.8,
          );
        };
      };
    });
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      setPreviewImage(URL.createObjectURL(file));
      setSelectedFile(await processImage(file));
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    const data = new FormData();
    data.append("name", formData.name);
    data.append("email", formData.email);
    data.append("mobile", formData.mobile);
    if (formData.password) data.append("password", formData.password);
    if (selectedFile) data.append("image", selectedFile);

    try {
      const id = storedUser?._id;
      const response = await dispatch(editUser({ id, data })).unwrap();
      console.log("response",response);
      localStorage.setItem("user", JSON.stringify(response.data));
    } catch (error) {
      // alert(t("profile.failed"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default", pb: 12 }}>
      <Container maxWidth="sm" sx={{ pt: 4 }}>
        <Paper
          elevation={0}
          sx={{
            p: 4,
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Typography variant="h5" fontWeight={1000} mb={4} textAlign="center">
            {t("profile.title")}
          </Typography>

          {/* الصورة الشخصية */}
          <Stack alignItems="center" mb={4}>
            <Box sx={{ position: "relative" }}>
              <Avatar
                src={
                  previewImage ||
                  (storedUser?.image && storedUser.image !== "null" && storedUser.image !== ""
                    ? `${baseUrl}${storedUser.image}`
                    : undefined)
                }
                sx={{
                  width: 110,
                  height: 110,
                  border: `4px solid ${brandRed}`,
                }}
              >
                {storedUser?.name?.[0]}
              </Avatar>
              <IconButton
                onClick={() => fileInputRef.current.click()}
                sx={{
                  position: "absolute",
                  bottom: 0,
                  right: isRtl ? "unset" : 0,
                  left: isRtl ? 0 : "unset",
                  bgcolor: "#000",
                  color: "#fff",
                  "&:hover": { bgcolor: brandRed },
                }}
              >
                <PhotoCamera fontSize="small" />
              </IconButton>
              <input
                type="file"
                hidden
                ref={fileInputRef}
                onChange={handleFileChange}
              />
            </Box>
          </Stack>

          {/* إعدادات سريعة (اللغة والمود) */}
          <Box
            sx={{
              bgcolor: alpha(theme.palette.divider, 0.05),
              p: 2,
              borderRadius: 2,
              mb: 4,
            }}
          >
            <Typography
              variant="overline"
              fontWeight={900}
              color="text.secondary"
              gutterBottom
              display="block"
            >
              {t("profile.settings")}
            </Typography>

            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ mb: 1 }}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <Language fontSize="small" color="primary" />
                <Typography variant="body2" fontWeight={700}>
                  {t("profile.language")}
                </Typography>
              </Stack>
              <Button
                size="small"
                onClick={toggleLanguage}
                sx={{ fontWeight: 900 }}
              >
                {isRtl ? "English" : "العربية"}
              </Button>
            </Stack>

            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Stack direction="row" spacing={1} alignItems="center">
                {theme.palette.mode === "dark" ? (
                  <DarkMode fontSize="small" color="primary" />
                ) : (
                  <LightMode fontSize="small" color="primary" />
                )}
                <Typography variant="body2" fontWeight={700}>
                  {t("profile.appearance")}
                </Typography>
              </Stack>
              <Switch
                checked={theme.palette.mode === "dark"}
                onChange={colorMode.toggleColorMode}
                color="primary"
              />
            </Stack>
          </Box>

          {/* حقول البيانات */}
          <Stack spacing={2.5}>
            <CustomTextField
              isRtl={isRtl}
              fullWidth
              label={t("profile.name")}
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              InputProps={{ disableUnderline: true, sx: { borderRadius: 2 } }}
              variant="filled"
            />
            <CustomTextField
              isRtl={isRtl}
              fullWidth
              label={t("profile.email")}
              value={formData.email}
              onChange={(e) =>
                setFormData({ ...formData, email: e.target.value })
              }
              InputProps={{ disableUnderline: true, sx: { borderRadius: 2 } }}
              variant="filled"
            />
            <CustomTextField
              isRtl={isRtl}
              fullWidth
              label={t("profile.mobile")}
              value={formData.mobile}
              onChange={(e) =>
                setFormData({ ...formData, mobile: e.target.value })
              }
              InputProps={{ disableUnderline: true, sx: { borderRadius: 2 } }}
              variant="filled"
            />

            <CustomTextField
              isRtl={isRtl}
              fullWidth
              type={showPassword ? "text" : "password"}
              label={t("profile.password")}
              onChange={(e) =>
                setFormData({ ...formData, password: e.target.value })
              }
              variant="filled"
              InputProps={{
                disableUnderline: true,
                sx: { borderRadius: 2 },
                ["endAdornment"]: (
                  <InputAdornment position={"end"}>
                    <IconButton onClick={() => setShowPassword(!showPassword)}>
                      {showPassword ? (
                        <VisibilityOff fontSize="small" />
                      ) : (
                        <Visibility fontSize="small" />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Button
              fullWidth
              variant="contained"
              size="large"
              onClick={handleSubmit}
              disabled={loading}
              startIcon={
                loading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <Save />
                )
              }
              sx={{ py: 1.8, borderRadius: 2, fontWeight: 900, mt: 1, gap:0.5 }}
            >
              {t("profile.save")}
            </Button>
          </Stack>

          <Divider sx={{ my: 4 }} />

          <Stack direction="row" spacing={2}>
            <Button
              fullWidth
              color="error"
              variant="outlined"
              onClick={() => setOpenDialog("logout")}
              sx={{ borderRadius: 2, fontWeight: 800 }}
            >
              {t("profile.logout")}
            </Button>
          </Stack>
        </Paper>
      </Container>

      {/* حوار التأكيد (نفس الكود السابق) */}
      <Dialog
        open={Boolean(openDialog)}
        onClose={() => setOpenDialog(null)}
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle fontWeight={900}>{t("profile.confirm_title")}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {openDialog === "delete"
              ? t("profile.confirm_delete")
              : t("profile.confirm_logout")}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ pb: 2, px: 2 }}>
          <Button onClick={() => setOpenDialog(null)} color="inherit">
            {t("profile.cancel")}
          </Button>
          <Button
            variant="contained"
            color={openDialog === "delete" ? "error" : "primary"}
            onClick={() => {
              if (openDialog === "logout") {
                localStorage.clear();
                navigate("/login");
              }
            }}
            sx={{ borderRadius: 2 }}
          >
            {t("profile.confirm")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
